import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as newspostsService from '../../services/api/newsposts';
import { useAuth } from '../../hooks/use-auth';
import { strings } from '../../localization/strings';
import { SimpleObject } from '../../entities/simple-object';
import { fillFromParams } from '../../services/crud/filter';
import UserContentFilter from './components/user-content-filter';
import FilterUserContent from '../../entities/filter/user-content';
import { NewspostStats } from '../../entities/newspost-stats';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonAnimatedLoader from '../../components/common/animated/loader';

function ReportsUserContentPageComponent() {
  const [queryParams, setQueryParams] = useSearchParams();

  const startDate = new Date();
  const endDate = new Date();

  startDate.setMonth(startDate.getMonth() - 1);
  startDate.setDate(1);

  endDate.setDate(0);

  const startFromLink = queryParams.get('starts_at') !== null ? new Date(queryParams.get('starts_at') as string) : null;

  const endFromLink = queryParams.get('ends_at') !== null ? new Date(queryParams.get('ends_at') as string) : null;

  const initialState: FilterUserContent = {
    starts_at: startFromLink || startDate,
    ends_at: endFromLink || endDate,
    page: 0,
    sort: '-created_at',
  };

  const { token } = useAuth();
  const [params, setParams] = useState<FilterUserContent>(
    fillFromParams<FilterUserContent>(initialState, queryParams, { isDate: ['starts_at', 'ends_at'] }),
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [stats, setStats] = useState<NewspostStats>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const items = await newspostsService.getStatsPerUserCategory(token, params);

        if (items) {
          setStats(items);
        }
        setIsLoading(false);
      } catch (e) {
        // handle your error
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [token, params]);

  const setFilterData = (filterData: FilterUserContent) => {
    const q: SimpleObject = {};

    // Cleanup undefined and null values. We don't want them in URLs
    for (const [key, value] of Object.entries(filterData)) {
      if (typeof value !== 'undefined' && value !== null) {
        q[key] = value;
      }
    }

    setParams({ ...filterData });
    setQueryParams({ ...q, page: 0 } as SimpleObject);
  };

  const exportData = async () => {
    const blob = await newspostsService.exportStatsPerUserCategory(token, params);

    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = 'stats-per-user-category.xlsx';
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
  };

  return (
    <>
      <header className="mb-3">
        <div className="flex space-x-3 justify-between">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">{strings.navigation.reports.content}</h1>
        </div>
      </header>
      <main className="w-full">
        <div className="sm:rounded-lg border border-gray-200 overflow-hidden">
          <UserContentFilter onUpdate={setFilterData} filterState={params} onExport={exportData} />
        </div>

        {error && (
          <div className="mx-auto my-5">
            <AlertPrimary type="danger" title={error} />
          </div>
        )}

        {isLoading && <CommonAnimatedLoader />}

        {!isLoading && !!stats && stats.stats.length > 0 ? (
          <div className="mt-8 flow-root border-top-1 border-top-gray-200">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.newsPostsStats.authors}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.newsPostsStats.total}
                      </th>
                      {stats.categories &&
                        stats.categories.length > 0 &&
                        stats.categories.map((category) => {
                          return (
                            <th
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              key={category.id}
                            >
                              {category.name}
                            </th>
                          );
                        })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.stats.map((item) => {
                      return (
                        <tr key={`stats-${item.id}`}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.name}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.total}</td>

                          {stats?.categories?.map((category) => {
                            const value = item?.categories?.[category.id] ?? 0;

                            return (
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" key={category.id}>
                                {value}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          !error && (
            <div className="my-5">
              <AlertPrimary type="failed" text={strings.search.noResults} />
            </div>
          )
        )}
      </main>
    </>
  );
}

export default ReportsUserContentPageComponent;
